import { Button, Flex, Stack, TextInput } from '@mantine/core';
import { ValueSetAutocomplete } from '@medplum/react';
import { useEffect, useMemo, useState } from 'react';
import { ContactPoint } from '@medplum/fhirtypes';
import { RELATED_PERSON_RELATIONSHIP_VALUESET_URL } from '@bariendo/lib/constants';
import { RelatedContactFormData } from './types';
import { alert } from '@/services/alert';
import { normalizePhoneNumber } from '@bariendo/lib/phone-validations';
import { getUUID } from '@bariendo/lib/services';

function getTelecomFromForm(phone: string, email: string): ContactPoint[] {
  const telecom: ContactPoint[] = [];
  if (phone?.trim()) {
    telecom.push({
      id: getUUID(),
      system: 'phone',
      use: 'mobile',
      value: normalizePhoneNumber(phone.trim()),
    });
  }
  if (email?.trim()) {
    telecom.push({
      id: getUUID(),
      system: 'email',
      use: 'work',
      value: email.trim(),
    });
  }
  return telecom;
}

function getFormFromTelecom(telecom?: RelatedContactFormData['telecom']): { phone: string; email: string } {
  const phone = telecom?.find((t) => t.system === 'phone')?.value ?? '';
  const email = telecom?.find((t) => t.system === 'email')?.value ?? '';
  return { phone, email };
}

type RelatedContactFormProps = {
  initialData?: RelatedContactFormData;
  onSave: (contact: RelatedContactFormData) => void;
  onClose: () => void;
};

export function RelatedContactForm({ initialData, onSave, onClose }: RelatedContactFormProps) {
  const initialPhoneEmail = useMemo(() => getFormFromTelecom(initialData?.telecom), [initialData?.telecom]);
  const [formData, setFormData] = useState<RelatedContactFormData | null>(initialData || null);
  const [phone, setPhone] = useState(initialPhoneEmail.phone);
  const [email, setEmail] = useState(initialPhoneEmail.email);

  // Sync phone/email when initialData changes (e.g. when switching between contacts)
  useEffect(() => {
    setPhone(initialPhoneEmail.phone);
    setEmail(initialPhoneEmail.email);
  }, [initialPhoneEmail.phone, initialPhoneEmail.email]);

  const handleSave = async () => {
    if (!formData?.name || !formData?.relationship) {
      alert.error({ message: 'Please fill in all fields from related contact' });
      return;
    }
    const telecom = getTelecomFromForm(phone, email);
    await onSave({ ...formData, telecom });
    onClose();
  };

  const initialCoding = useMemo(() => (formData?.relationship?.code ? [formData.relationship] : undefined), [formData]);

  return (
    <Stack gap={8} mb={8}>
      <TextInput
        label="Name"
        placeholder="Contact name"
        value={formData?.name}
        onChange={(e) => setFormData({ ...formData, name: e.currentTarget.value } as RelatedContactFormData)}
        required
      />
      <ValueSetAutocomplete
        key={`${initialData?.relationship?.system || ''}-${initialData?.relationship?.code || 'new'}`} // Reset component when switching between contacts
        label="Relationship"
        placeholder="Select relationship"
        binding={RELATED_PERSON_RELATIONSHIP_VALUESET_URL}
        maxValues={1}
        defaultValue={initialCoding}
        required
        onChange={(codings) => {
          setFormData({ ...formData, relationship: codings?.[0] } as RelatedContactFormData);
        }}
      />
      <TextInput
        label="Phone"
        placeholder="+1 (555) 123-4567"
        value={phone}
        onChange={(e) => setPhone(e.currentTarget.value)}
      />
      <TextInput
        label="Email"
        placeholder="email@example.com"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.currentTarget.value)}
      />

      <Flex justify="flex-end" gap={8}>
        <Button variant="subtle" color="blue" radius="sm" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="blue"
          radius="lg"
          size="sm"
          onClick={handleSave}
          disabled={!formData?.name || !formData?.relationship}
        >
          Save
        </Button>
      </Flex>
    </Stack>
  );
}
